/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { DraggableSize, HeaderSize, Position } from '@opengeoweb/shared';
import { ToolContainerDraggable, calculateStartSize } from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import { useWarningsTranslation } from '../../../utils/i18n';

const DEFAULT_SIZE = { width: 1150, height: 950 };
const DEFAULT_POSITION = { top: 8, left: 15 };
const DEFAULT_MIN_SIZE = { width: 600, height: 400 };

export interface WarningEventDialogProps {
  bounds?: string;
  isOpen: boolean;
  onClose: () => void;
  onMouseDown?: () => void;
  order?: number;
  source?: uiTypes.Source;
  size?: DraggableSize;
  startPosition?: Position;
  headerSize?: HeaderSize;
  children: React.ReactNode;
}

export const WarningEventDialog: React.FC<WarningEventDialogProps> = ({
  bounds,
  isOpen,
  onClose,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  size = DEFAULT_SIZE,
  startPosition = DEFAULT_POSITION,
  headerSize = 'xs',
  children,
}) => {
  const { t } = useWarningsTranslation();
  const startSizeCalc =
    size === DEFAULT_SIZE
      ? calculateStartSize(DEFAULT_MIN_SIZE, size, startPosition)
      : {
          width: Math.max(DEFAULT_MIN_SIZE.width, size.width),
          height: Math.max(DEFAULT_MIN_SIZE.height, size.height),
        };
  const [sizeInState, setSizeInState] =
    React.useState<DraggableSize>(startSizeCalc);

  return (
    <ToolContainerDraggable
      bounds={bounds}
      dragHandleIcon={null}
      headerSize={headerSize}
      isOpen={isOpen}
      minHeight={DEFAULT_MIN_SIZE.height}
      minWidth={DEFAULT_MIN_SIZE.width}
      onClose={onClose}
      onDragEnd={(_position, dragSize): void => {
        if (dragSize !== sizeInState) {
          setSizeInState(dragSize as DraggableSize);
        }
      }}
      onMouseDown={onMouseDown}
      onResizeStop={(_event, _direction, node): void => {
        const { offsetWidth: width, offsetHeight: height } = node;
        setSizeInState({ width, height });
      }}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={sizeInState}
      title={t('public-warning-event-title')}
    >
      {children}
    </ToolContainerDraggable>
  );
};
